import { sessionStorageProxy } from "./window.storage";
import { HttpServiceResponse } from "./httpService.interface";

export type ResponseValidator<D> = (response: CachedResponse<D>) => boolean;

export interface CachedResponse<D> {
  data: D;
  createdAt: string;
}

export class ResponseCache<D> {
  private storage = sessionStorageProxy;

  constructor(
    private cacheName: string,
    private responseValidator: ResponseValidator<D>
  ) {}

  private getCache(): Record<string, CachedResponse<D>> | null {
    const cache = this.storage.getItem(this.cacheName);

    return cache ? JSON.parse(cache) : null;
  }

  private match(cacheKey: string): CachedResponse<D> | null {
    const cache = this.getCache();

    return cache ? cache[cacheKey] : null;
  }

  private transformResponse(
    response: HttpServiceResponse<D>
  ): CachedResponse<D> {
    return {
      data: response.data,
      createdAt: response.headers["date"],
    };
  }

  private put(
    cacheKey: string,
    response: HttpServiceResponse<D>
  ): CachedResponse<D> {
    const cache = this.getCache();
    const cachedResponse = this.transformResponse(response);
    const newEntry = { [cacheKey]: cachedResponse };

    const newCache = JSON.stringify({ ...cache, ...newEntry });

    this.storage.setItem(this.cacheName, newCache);

    return cachedResponse;
  }

  public getResponse(cacheKey: string): CachedResponse<D> | null {
    const cachedResponse = this.match(cacheKey);
    const isStale = cachedResponse && this.responseValidator(cachedResponse);

    if (cachedResponse && !isStale) {
      return cachedResponse;
    }

    return null;
  }

  public saveResponse(
    cacheKey: string,
    response: HttpServiceResponse<D>
  ): CachedResponse<D> {
    return this.put(cacheKey, response);
  }
}
